import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_BUDGET,
  SAB,
  type Blob,
  type Line,
  type Rect,
  type RobotPose,
  type SimError,
  type SimEvent,
  type StdoutChunk,
  type WorkerMessage,
} from "./protocol";

export type SimStatus = "loading" | "ready" | "running" | "stopped";

export type SimulatorState = {
  status: SimStatus;
  events: SimEvent[];
  stdout: StdoutChunk[];
  error: SimError | null;
  elapsedSimMs: number;
  liveAvailable: boolean; // true when SharedArrayBuffer live mode is usable
  run: (code: string, speed: "realtime" | "instant") => void;
  stop: () => void;
  clearLog: () => void;
  setUltrasonic: (cm: number | null) => void;
  setImu: (xAngleDeg: number) => void;
  setCameraBlob: (colorId: number, blob: Blob) => void;
  setWorld: (obstacles: Rect[], robot: RobotPose) => void;
  setLines: (lines: Line[]) => void;
};

// The worker owns the interpreter; the main thread owns the wall-clock watchdog
// and the two-stage Stop (only terminate() can end a blocked synchronous
// instant run). Worker/watchdog helpers live inside the mount effect and are
// bridged to run()/stop() via refs to avoid a useCallback dependency cycle.
export function useSimulator(): SimulatorState {
  const [status, setStatus] = useState<SimStatus>("loading");
  const [events, setEvents] = useState<SimEvent[]>([]);
  const [stdout, setStdout] = useState<StdoutChunk[]>([]);
  const [error, setError] = useState<SimError | null>(null);
  const [elapsedSimMs, setElapsed] = useState(0);

  const workerRef = useRef<Worker | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armRef = useRef<() => void>(() => {});
  const respawnRef = useRef<() => void>(() => {});

  // LIVE mode shared buffer (only when cross-origin isolated). ctrl = Atomics
  // control (futex + stop flag); vals = live sensor values written from here and
  // read by the running program. liveRunRef tracks whether the current run is live.
  const sabRef = useRef<SharedArrayBuffer | null>(null);
  const ctrlRef = useRef<Int32Array | null>(null);
  const valsRef = useRef<Float64Array | null>(null);
  const liveRunRef = useRef(false);
  if (sabRef.current === null && typeof SharedArrayBuffer !== "undefined" && crossOriginIsolated) {
    const sab = new SharedArrayBuffer(SAB.bytes);
    sabRef.current = sab;
    ctrlRef.current = new Int32Array(sab, 0, SAB.ctrlLen);
    valsRef.current = new Float64Array(sab, SAB.valuesByteOffset, SAB.valuesLen);
    valsRef.current[SAB.VAL_ULTRASONIC] = NaN;
    valsRef.current[SAB.VAL_IMU] = 90;
  }

  // Latest world/sensor inputs — re-sent whenever a (re)spawned worker becomes
  // ready, since a fresh interpreter loses the persisted environment.
  const sensorRef = useRef<{
    ultrasonic: number | null;
    imu: number;
    blobs: Record<number, Blob>;
    obstacles: Rect[];
    robot: RobotPose;
    lines: Line[];
  }>({
    ultrasonic: null,
    imu: 90,
    blobs: {},
    obstacles: [],
    robot: { x: 0, y: 0, heading: 0 },
    lines: [],
  });

  useEffect(() => {
    function clearTimers() {
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      watchdogRef.current = null;
      stopTimerRef.current = null;
    }

    function armWatchdog() {
      // Live real-time runs legitimately take real wall-time; the user watches
      // and stops them, so the "never sleeps" watchdog doesn't apply.
      if (liveRunRef.current) return;
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      watchdogRef.current = setTimeout(() => {
        respawn();
        setStatus("stopped");
        setError({
          kind: "budget",
          line: null,
          message:
            "Your loop never sleeps — on the real robot this would freeze it. " +
            "Add time.sleep_ms() inside the loop.",
        });
      }, DEFAULT_BUDGET.wallClockMs);
    }

    function resendSensors() {
      const w = workerRef.current;
      if (!w) return;
      const s = sensorRef.current;
      w.postMessage({
        type: "sensorSet",
        sensor: "world",
        value: { obstacles: s.obstacles, robot: s.robot },
      });
      w.postMessage({ type: "sensorSet", sensor: "ultrasonic", value: s.ultrasonic });
      w.postMessage({ type: "sensorSet", sensor: "imu", value: s.imu });
      w.postMessage({ type: "sensorSet", sensor: "lines", value: { lines: s.lines } });
      for (const [id, blob] of Object.entries(s.blobs)) {
        w.postMessage({
          type: "sensorSet",
          sensor: "camera",
          value: { colorId: Number(id), blob },
        });
      }
    }

    function attach(worker: Worker) {
      worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
        const msg = e.data;
        switch (msg.type) {
          case "ready":
            setStatus((s) => (s === "loading" ? "ready" : s));
            resendSensors();
            break;
          case "stdout":
            setStdout((prev) => [...prev, { t: msg.t, text: msg.text }]);
            armWatchdog();
            break;
          case "event":
            setEvents((prev) => [...prev, msg.event]);
            armWatchdog();
            break;
          case "error":
            setError(msg.error);
            break;
          case "done":
            if (msg.elapsedSimMs) setElapsed(msg.elapsedSimMs);
            clearTimers();
            setStatus("stopped");
            break;
        }
      };
    }

    function spawn() {
      const worker = new Worker(new URL("./mp-worker.ts", import.meta.url), { type: "module" });
      attach(worker);
      workerRef.current = worker;
      worker.postMessage({ type: "init", sab: sabRef.current }); // enable LIVE mode if isolated
    }

    function respawn() {
      clearTimers();
      workerRef.current?.terminate();
      setStatus("loading");
      spawn();
    }

    armRef.current = armWatchdog;
    respawnRef.current = respawn;
    spawn();

    return () => {
      clearTimers();
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const run = useCallback((code: string, speed: "realtime" | "instant") => {
    const worker = workerRef.current;
    if (!worker) return;
    // Live only when the browser is cross-origin isolated (SharedArrayBuffer).
    liveRunRef.current = speed === "realtime" && ctrlRef.current !== null;
    if (ctrlRef.current) Atomics.store(ctrlRef.current, SAB.CTRL_STOP, 0); // clear any prior Stop
    setEvents([]);
    setStdout([]);
    setError(null);
    setElapsed(0);
    setStatus("running");
    armRef.current(); // no-op for live runs
    worker.postMessage({ type: "run", code, speed: speed === "realtime" ? 1 : 0 });
  }, []);

  const stop = useCallback(() => {
    const worker = workerRef.current;
    if (!worker) return;
    if (liveRunRef.current && ctrlRef.current) {
      // Stage 1 (live): signal Stop via the shared buffer and wake the paced
      // worker; its next sleep raises KeyboardInterrupt so the script's handler runs.
      Atomics.store(ctrlRef.current, SAB.CTRL_STOP, 1);
      Atomics.notify(ctrlRef.current, SAB.CTRL_WAIT);
    } else {
      worker.postMessage({ type: "stop" }); // stage 1 (replay): graceful between ticks
    }
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = setTimeout(() => {
      respawnRef.current(); // stage 2: hard reset if it didn't stop in time
      setStatus("stopped");
    }, 1000);
  }, []);

  const clearLog = useCallback(() => {
    setEvents([]);
    setStdout([]);
    setError(null);
  }, []);

  const send = useCallback((sensor: string, value: unknown) => {
    workerRef.current?.postMessage({ type: "sensorSet", sensor, value });
  }, []);

  const setUltrasonic = useCallback(
    (cm: number | null) => {
      sensorRef.current.ultrasonic = cm;
      // LIVE: write straight to shared memory so a running program sees it now.
      if (valsRef.current) valsRef.current[SAB.VAL_ULTRASONIC] = cm === null ? NaN : cm;
      send("ultrasonic", cm); // also the message path (instant / non-isolated)
    },
    [send],
  );
  const setImu = useCallback(
    (xAngleDeg: number) => {
      sensorRef.current.imu = xAngleDeg;
      if (valsRef.current) valsRef.current[SAB.VAL_IMU] = xAngleDeg;
      send("imu", xAngleDeg);
    },
    [send],
  );
  const setCameraBlob = useCallback(
    (colorId: number, blob: Blob) => {
      sensorRef.current.blobs[colorId] = blob;
      send("camera", { colorId, blob });
    },
    [send],
  );
  const setLines = useCallback(
    (lines: Line[]) => {
      sensorRef.current.lines = lines;
      send("lines", { lines });
    },
    [send],
  );
  const setWorld = useCallback(
    (obstacles: Rect[], robot: RobotPose) => {
      sensorRef.current.obstacles = obstacles;
      sensorRef.current.robot = robot;
      send("world", { obstacles, robot });
    },
    [send],
  );

  return {
    status,
    events,
    stdout,
    error,
    elapsedSimMs,
    liveAvailable: ctrlRef.current !== null,
    run,
    stop,
    clearLog,
    setUltrasonic,
    setImu,
    setCameraBlob,
    setWorld,
    setLines,
  };
}
